// src/index.ts
import { createContext, createSignal, createEffect, onCleanup, getOwner, useContext } from "solid-js";
import {
  liveQuery,
  liveFilterAndMap,
  liveQueryAndMap,
  liveQueryEntity,
  liveEntityAt,
  liveQuerySingle,
  liveQuerySingleAndMap,
  liveEntityOverlapCount,
  liveEntityCollection
} from "@wovin/core/query";
import { rollingAcc } from "@wovin/core/thread";
var ThreadContext = createContext();
var _scopeThread = null;
function useThread() {
  if (_scopeThread) return _scopeThread;
  const fromContext = useContext(ThreadContext);
  if (!fromContext) throw new Error("@wovin/hooks-solid: no thread \u2014 use <ThreadContext.Provider> or withThread()");
  return fromContext();
}
function withThread(thread, fn) {
  const prev = _scopeThread;
  _scopeThread = thread;
  const result = fn();
  _scopeThread = prev;
  return result;
}
function resolveThread(opts) {
  return opts?.thread ?? useThread();
}
function useSubscribable(source) {
  const [data, setData] = createSignal(source.items, { equals: false });
  const unsub = source.subscribe(() => {
    setData(() => source.items);
  });
  if (getOwner()) {
    onCleanup(() => {
      unsub();
    });
  }
  return data;
}
function useSubscribableMemo(factory) {
  const [data, setData] = createSignal([], { equals: false });
  let currentUnsub = null;
  createEffect(() => {
    const source = factory();
    currentUnsub?.();
    setData(() => source.items);
    currentUnsub = source.subscribe(() => {
      setData(() => source.items);
    });
  });
  if (getOwner()) {
    onCleanup(() => {
      currentUnsub?.();
    });
  }
  return data;
}
function useSubscribableValue(source) {
  const [data, setData] = createSignal(source.value, { equals: false });
  const unsub = source.subscribe(() => {
    setData(() => source.value);
  });
  if (getOwner()) {
    onCleanup(() => {
      unsub();
    });
  }
  return data;
}
function useSubscribableValueMemo(factory, initialValue) {
  const [data, setData] = createSignal(initialValue, { equals: false });
  let currentUnsub = null;
  createEffect(() => {
    const source = factory();
    currentUnsub?.();
    setData(() => source.value);
    currentUnsub = source.subscribe(() => {
      setData(() => source.value);
    });
  });
  if (getOwner()) {
    onCleanup(() => {
      currentUnsub?.();
    });
  }
  return data;
}
function useEternalSubscribableValue(source) {
  const [get, set] = createSignal(source.value, { equals: false });
  source.subscribe(() => set(() => source.value));
  return get;
}
function useEternalSubscribable(source) {
  const [data, setData] = createSignal(source.items, { equals: false });
  source.subscribe(() => setData(() => source.items));
  return data;
}
function isAccessor(v) {
  return typeof v === "function";
}
function useLiveQuery(patterns, opts) {
  const thread = resolveThread(opts);
  if (isAccessor(patterns)) {
    return useLiveQueryMemoInternal(thread, patterns, opts?.startVariables);
  }
  return useLiveQueryStatic(thread, patterns, opts?.startVariables);
}
function useLiveQueryStatic(thread, patterns, startVariables) {
  const source = liveQuery(thread, patterns, startVariables);
  const [data, setData] = createSignal(source.nodes, { equals: false });
  const unsub = source.subscribe(() => {
    setData(() => source.nodes);
  });
  if (getOwner()) {
    onCleanup(() => {
      unsub();
    });
  }
  return data;
}
function useLiveQueryMemoInternal(thread, patterns, startVariables) {
  const [data, setData] = createSignal([], { equals: false });
  let currentUnsub = null;
  createEffect(() => {
    const p = patterns();
    currentUnsub?.();
    const source = liveQuery(thread, p, startVariables);
    setData(() => source.nodes);
    currentUnsub = source.subscribe(() => {
      setData(() => source.nodes);
    });
  });
  if (getOwner()) {
    onCleanup(() => {
      currentUnsub?.();
    });
  }
  return data;
}
function useLiveQueryEntity(name, entityID, attributes, opts) {
  const thread = resolveThread(opts);
  if (isAccessor(entityID)) {
    return useSubscribableValueMemo(() => liveQueryEntity(thread, name, entityID(), attributes));
  }
  return useSubscribableValue(liveQueryEntity(thread, name, entityID, attributes));
}
function useLiveFilterAndMap(pattern, mapper, opts) {
  const thread = resolveThread(opts);
  if (isAccessor(pattern)) {
    return useSubscribableMemo(() => liveFilterAndMap(thread, pattern(), mapper));
  }
  return useSubscribable(liveFilterAndMap(thread, pattern, mapper));
}
function useLiveQueryAndMap(patterns, mapDef, opts) {
  const thread = resolveThread(opts);
  if (isAccessor(patterns)) {
    return useSubscribableMemo(() => liveQueryAndMap(thread, patterns(), mapDef));
  }
  return useSubscribable(liveQueryAndMap(thread, patterns, mapDef));
}
function useLiveQuerySingle(patterns, opts) {
  const thread = resolveThread(opts);
  if (isAccessor(patterns)) {
    return useSubscribableValueMemo(() => liveQuerySingle(thread, patterns()));
  }
  return useSubscribableValue(liveQuerySingle(thread, patterns));
}
function useLiveQuerySingleAndMap(patterns, mapDef, opts) {
  const thread = resolveThread(opts);
  if (isAccessor(patterns)) {
    return useSubscribableValueMemo(() => liveQuerySingleAndMap(thread, patterns(), mapDef));
  }
  return useSubscribableValue(liveQuerySingleAndMap(thread, patterns, mapDef));
}
function useLiveEntityOverlapCount(threadA, threadB) {
  return useSubscribableValue(liveEntityOverlapCount(threadA, threadB));
}
function useLiveEntityCollection(discoveryPattern, liveAttributes, opts) {
  const thread = resolveThread(opts);
  const { stripAtPrefix, mapKeys } = opts ?? {};
  return useSubscribableValue(
    liveEntityCollection(thread, discoveryPattern, liveAttributes, stripAtPrefix || mapKeys ? { stripAtPrefix, mapKeys } : void 0)
  );
}
function useLiveEntityAt(entityID, at, opts) {
  const thread = resolveThread(opts);
  if (isAccessor(entityID)) {
    return useSubscribableValueMemo(() => liveEntityAt(thread, entityID(), at));
  }
  return useSubscribableValue(liveEntityAt(thread, entityID, at));
}
function useRollingAcc(acc, eventMapper, opts) {
  const thread = resolveThread(opts);
  return useSubscribableValue(rollingAcc(thread, acc, eventMapper, opts));
}
export {
  ThreadContext,
  useEternalSubscribable,
  useEternalSubscribableValue,
  useLiveEntityAt,
  useLiveEntityCollection,
  useLiveEntityOverlapCount,
  useLiveFilterAndMap,
  useLiveQuery,
  useLiveQueryAndMap,
  useLiveQueryEntity,
  useLiveQuerySingle,
  useLiveQuerySingleAndMap,
  useRollingAcc,
  useSubscribable,
  useSubscribableMemo,
  useSubscribableValue,
  useSubscribableValueMemo,
  useThread,
  withThread
};
//# sourceMappingURL=index.js.map